// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { ExtractedDocument } from '../types/extraction';
import { loadPdf } from './pdfTextLoader';
import { detectFormat, fingerprintDocument } from './formatDetector';
import { extractAnnualStatement } from './standardLifeAnnualExtractor';
import { extractTransactionsStatement } from './standardLifeTransactionsExtractor';

/**
 * Public entry point for the frontend statement extractor.
 *
 * Loads the PDF via pdfjs-dist (lazy), fingerprints the format, and runs
 * the matching extractor. Returns a discriminated `ExtractedDocument` so
 * callers can switch on `kind`.
 */
export async function extractDocument(file: File): Promise<ExtractedDocument> {
  const pdf = await loadPdf(file);
  const { kind, imageBasedWarning } = detectFormat(pdf);
  const sourceFileName = file.name;

  if (kind === 'STANDARD_LIFE_ANNUAL') {
    return {
      kind,
      fields: extractAnnualStatement(pdf),
      documentFingerprint: fingerprintDocument(pdf),
      sourceFileName,
    };
  }

  if (kind === 'STANDARD_LIFE_TRANSACTIONS') {
    return {
      kind,
      transactions: extractTransactionsStatement(pdf),
      documentFingerprint: fingerprintDocument(pdf),
      sourceFileName,
    };
  }

  return {
    kind: 'UNKNOWN',
    reason:
      imageBasedWarning ??
      'Unrecognised provider format. Please continue manually.',
    sourceFileName,
  };
}

export { applyExtraction } from './applyExtraction';
export type { AppliedExtractionPatch } from './applyExtraction';
